import { CloseOutlined } from '@ant-design/icons';
import { Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, Stack } from '@mui/material';
import { PopupTransition } from 'components/@extended/Transitions';
import AsyncSelectSearchFormField from '../async-select-search-form-field';
import axiosServices from 'utils/axios';
import UserGroupDto from 'common/dto-types/user-group-dto';
import { Form, FormikProvider, useFormik } from 'formik';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import { SynchronizeUserGroup } from './api';

type SynchronizeUserGroupsDialogProps = {
    isOpen: boolean;
    handleClose: () => void;
};
type SynchronizeUserGroupsValues = {
    userGroup: UserGroupDto | null;
};

const SynchronizeUserGroupsDialog = ({ isOpen, handleClose }: SynchronizeUserGroupsDialogProps) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const initialFormValue: SynchronizeUserGroupsValues = {
        userGroup: null,
    };
    const onSubmit = async () => {
        setIsLoading(true);
        if (values.userGroup != null)
            try {
                const result = await SynchronizeUserGroup(values.userGroup?.id);
                if (result?.status === 200) {
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: 'User Group Synchronized',
                            variant: 'alert',
                            alert: {
                                color: 'success',
                            },
                        }),
                    );
                } else {
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: 'Failed to synchronize user group',
                            variant: 'alert',
                            alert: {
                                color: 'error',
                            },
                        }),
                    );
                }
            } catch {
                dispatch(
                    openSnackbar({
                        open: true,
                        message: 'Failed to synchronize user group',
                        variant: 'alert',
                        alert: {
                            color: 'error',
                        },
                    }),
                );
            } finally {
                setIsLoading(false);
                handleClose();
            }
    };

    const formik = useFormik<SynchronizeUserGroupsValues>({
        initialValues: initialFormValue,
        onSubmit,
    });

    const { values, isValid, handleSubmit } = formik;

    return (
        <FormikProvider value={formik}>
            <Dialog
                maxWidth="md"
                TransitionComponent={PopupTransition}
                fullWidth
                open={isOpen}
                sx={{ maxHeight: '100vh', '& .MuiDialog-paper': { p: 0 } }}
            >
                <Form onSubmit={handleSubmit}>
                    <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                        Synchronize User Groups Dialog
                    </DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseOutlined />
                    </IconButton>
                    <DialogContent dividers>
                        <Grid item xs={12}>
                            <Stack spacing={1.25}>
                                <AsyncSelectSearchFormField
                                    label="User Group"
                                    name="userGroup"
                                    fetchData={(inputValue) =>
                                        axiosServices
                                            .get(
                                                `userGroups${
                                                    inputValue && `?andSpecifications=NameSpecification:${inputValue}`
                                                }`,
                                            )
                                            .then((res) => res.data.elements)
                                    }
                                    getOptionLabel={(option: UserGroupDto) => option.name ?? ''}
                                    isOptionEqualToValue={(option, selectedValue) => {
                                        return option.id === selectedValue.id;
                                    }}
                                    filterOptions={(x) => x}
                                    multiple={false}
                                />
                            </Stack>
                        </Grid>
                    </DialogContent>
                    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ px: 2.5, py: 2 }}>
                        <Button color="error" size="small" disabled={isLoading} onClick={handleClose}>
                            Cancel
                        </Button>
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            size="small"
                            loading={isLoading}
                            disabled={!isValid || !values.userGroup}
                        >
                            Synchronize
                        </LoadingButton>
                    </Stack>
                </Form>
            </Dialog>
        </FormikProvider>
    );
};

export default SynchronizeUserGroupsDialog;
